import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { CommonModule, NgFor, NgIf } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

export enum ConfirmMessageType {
  Default = 'default',
  Bulleted = 'bulleted',
}

/**
 * Represents the data structure for the confirm dialog.
 */
export interface ConfirmDialogData {
  /**
   * The title of the confirm dialog.
   */
  title: string;

  /**
   * An optional array of messages to display in the confirm dialog.
   */
  messages?: string[];

  /**
   * Specifies whether the messages should be displayed as bulleted list.
   */
  messageType?: ConfirmMessageType;
  /**
   * The text to display on the confirm button.
   */
  confirmText?: string;

  /**
   * The value associated with the confirm button.
   */
  confirmValue?: boolean;

  /**
   * The text to display on the cancel button.
   */
  cancelText?: string;

  /**
   * The value associated with the cancel button.
   */
  cancelValue?: boolean;
}

/**
 * Represents a confirm dialog component.
 */
@Component({
  selector: 'cbre-confirm',
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, NgIf, NgFor],
  template: `
    <div class="dialog confirm-dialog cbre-confirm-dialog">
      <div mat-dialog-title>
        <h1>{{ title }}</h1>
      </div>

      <div mat-dialog-content class="confirm-container">
        <div class="viewmode">
          <mat-icon><em class="fa fa-exclamation-triangle"></em></mat-icon>
          <div class="alert-msgs">
            <!-- CONFIRM MESSAGES -->
            <ng-container *ngIf="data.messageType === 'bulleted'; else defaultMultiMessage">
              <!-- BULLETED MULTI MESSAGE -->
              <ul class="bulleted-multi-message">
                <li *ngFor="let message of data.messages">{{ message }}</li>
              </ul>
            </ng-container>
            <!-- DEFAULT MULTI MESSAGE -->
            <ng-template #defaultMultiMessage>
              <p class="message" *ngFor="let message of data.messages">{{ message }}</p>
            </ng-template>
          </div>
        </div>

        <div mat-dialog-actions>
          <ng-container *ngIf="data.cancelText">
            <button id="e2e-cancel-dialog" mat-button color="primary" (click)="close(cancelValue)">
              {{ data.cancelText }}
            </button>
          </ng-container>
          <ng-container>
            <button
              id="e2e-confirm-dialog"
              mat-flat-button
              color="primary"
              (click)="close(confirmValue)"
              cdkFocusInitial
            >
              {{ confirmText }}
            </button>
          </ng-container>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./confirm-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmDialogComponent {
  /**
   * The title of the confirm dialog.
   */
  title = 'Confirm';

  /**
   * The text to display on the confirm button.
   */
  confirmText = 'Ok';

  /**
   * The value associated with the confirm button.
   */
  confirmValue = true;

  /**
   * The value associated with the cancel button.
   */
  cancelValue = null;

  /**
   * Creates an instance of ConfirmDialogComponent.
   * @param dialogRef - The reference to the dialog.
   * @param data - The data for the confirm dialog.
   */
  constructor(
    public dialogRef: MatDialogRef<ConfirmDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConfirmDialogData
  ) {
    this._setData(data);
  }

  /**
   * Sets the data for the confirm dialog.
   * @param data - The data for the confirm dialog.
   * @private
   */
  private _setData(data: ConfirmDialogData) {
    this.title = data.title ?? this.title;
    this.confirmText = data.confirmText ?? this.confirmText;
    this.confirmValue = data.confirmValue ?? true;
    this.cancelValue = data.cancelValue ?? false;
  }

  /**
   * Selects an option and closes the dialog.
   * @param value - The value associated with the selected option.
   */
  public close(value: boolean) {
    this.dialogRef.close(value);
  }
}
